import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, Star, Shield, Brain, 
  ArrowUpRight, BarChart3, DollarSign, Calendar, FileText, CheckCircle, XCircle 
} from 'lucide-react';

// Mock dashboard data reflecting actual features
const kpiData = [
  { label: 'Rate Parity Score', value: '94%', change: '+2.1%', trend: 'up', icon: Shield },
  { label: 'Avg Direct Rate', value: '₹8,500', change: '+8%', trend: 'up', icon: DollarSign },
  { label: 'Market Position', value: '#2', change: '+1', trend: 'up', icon: BarChart3 },
  { label: 'OTA Undercuts', value: '3', change: '-2', trend: 'down', icon: TrendingDown },
];

const otaRates = [
  { name: 'Direct', rate: 8500, status: 'parity', icon: CheckCircle },
  { name: 'MakeMyTrip', rate: 8200, status: 'undercut', icon: XCircle },
  { name: 'Booking.com', rate: 8500, status: 'parity', icon: CheckCircle },
  { name: 'Goibibo', rate: 8100, status: 'undercut', icon: XCircle },
  { name: 'Agoda', rate: 8500, status: 'parity', icon: CheckCircle },
];

const aiInsights = [
  "Consider increasing rates by 8-12% for weekend dates based on demand patterns",
  "MakeMyTrip showing consistent undercuts - recommend channel manager review",
  "Upcoming Tech Conference (Mar 15-17) may boost demand by 25%",
];

export default function DashboardShowcase() {
  const [viewMode, setViewMode] = useState('rate-parity');

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">
            Live Dashboard Preview
          </p>
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Your Revenue Command Center
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Monitor rate parity, track competitors, and get AI-powered insights—all in one powerful dashboard.
          </p>

          {/* View Toggle */}
          {/* <div className="inline-flex items-center gap-1 p-1 bg-muted rounded-lg">
            <Button
              variant={viewMode === 'rate-parity' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('rate-parity')}
            >
              <Shield className="w-4 h-4 mr-2" />
              Rate Parity
            </Button>
            <Button
              variant={viewMode === 'ai-insights' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('ai-insights')}
            >
              <Brain className="w-4 h-4 mr-2" />
              AI Insights
            </Button>
          </div> */}
        </div>

        {/* Dashboard Container */}
        <div className="bg-card rounded-2xl shadow-xl border border-border overflow-hidden">
          {/* Dashboard Header */}
          <div className="bg-muted/50 border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-sm text-muted-foreground">Prowen Analytics Dashboard</span>
            </div>
            <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
              Live Data
            </Badge>
          </div>

          <div className="p-6 lg:p-8">
            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {kpiData.map((kpi, index) => (
                <Card key={index} className="bg-muted/30 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <kpi.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-medium ${
                        kpi.trend === 'up' ? 'text-success' : kpi.label === 'OTA Undercuts' ? 'text-success' : 'text-destructive'
                      }`}>
                        {kpi.trend === 'up' ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {kpi.change}
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-foreground mb-1">{kpi.value}</p>
                    <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* OTA Rate Parity */}
              <Card className="bg-muted/30 border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      OTA Rate Parity
                    </CardTitle>
                    <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/30">
                      Real-time
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {otaRates.map((ota, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          ota.name === 'Direct' ? 'bg-primary/10 border border-primary/20' : 'bg-background/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <ota.icon className={`w-5 h-5 ${
                            ota.status === 'parity' ? 'text-success' : 'text-destructive'
                          }`} />
                          <p className={`text-sm font-medium ${
                            ota.name === 'Direct' ? 'text-primary' : 'text-foreground'
                          }`}>
                            {ota.name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">₹{ota.rate.toLocaleString()}</p>
                          <p className={`text-xs ${
                            ota.status === 'parity' ? 'text-success' : 'text-destructive'
                          }`}>
                            {ota.status === 'parity' ? 'At Parity' : 'Undercut'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* AI Insights */}
              <Card className="bg-muted/30 border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-500" />
                      AI Insights
                    </CardTitle>
                    <Badge className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                      Prowen AI
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {aiInsights.map((insight, index) => (
                      <div key={index} className="bg-background/50 rounded-lg p-4 border-l-4 border-purple-500">
                        <p className="text-sm text-foreground leading-relaxed">{insight}</p>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-4 gap-2">
                    <FileText className="w-4 h-4" />
                    Generate PDF Report
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Demand Calendar Preview */}
            <div className="mt-6">
              <Card className="bg-muted/30 border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-amber-500" />
                      Demand & Events Calendar
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="text-primary">
                      View Full Calendar <ArrowUpRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { date: 'Mar 15-17', event: 'Tech Conference', impact: 'High', color: 'text-red-500' },
                      { date: 'Mar 21', event: 'City Marathon', impact: 'Medium', color: 'text-amber-500' },
                      { date: 'Mar 25', event: 'Holi Festival', impact: 'High', color: 'text-red-500' },
                      { date: 'Apr 1-3', event: 'Business Summit', impact: 'Medium', color: 'text-amber-500' },
                    ].map((event, index) => (
                      <div key={index} className="bg-background/50 rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">{event.date}</p>
                        <p className="text-sm font-medium text-foreground mb-2">{event.event}</p>
                        <Badge variant="outline" className={`text-xs ${event.color}`}>
                          {event.impact} Demand
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Annotation */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Export to PDF • Real-time data updates
        </p>
      </div>
    </section>
  );
}
